import { useState } from 'react';
import useConfigurationStore from '../store/configurationStore';
import { formatPrice } from '../utils/priceCalculator';
import RequestSupplierQuoteModal from './RequestSupplierQuoteModal';

function renderNotes(opt, notes) {
    if (!notes) return null;
    if (typeof notes === 'string') return notes;
    if (typeof notes === 'object') {
        if (notes.value !== undefined && Object.keys(notes).length === 1) {
            return String(notes.value);
        }
        let subFields = [];
        if (opt.sub_fields) {
            try {
                subFields = typeof opt.sub_fields === 'string' ? JSON.parse(opt.sub_fields) : opt.sub_fields;
            } catch { /* ignore */ }
        }
        const labelMap = {};
        subFields.forEach((f) => { labelMap[f.key] = f.label; });
        return Object.entries(notes)
            .filter(([, v]) => v !== '' && v !== null && v !== undefined)
            .map(([key, val]) => {
                const label = labelMap[key] || key;
                const display = Array.isArray(val) ? val.join(', ') : typeof val === 'boolean' ? (val ? 'Ja' : 'Nee') : String(val);
                return <div key={key} className="ast-summary__note-line"><span className="ast-summary__note-label">{label}:</span> {display}</div>;
            });
    }
    return String(notes);
}

export default function SummaryStep() {
    const {
        categories, optionsByCategory, selectedOptions, optionNotes, priceBreakdown,
        configNotes, setConfigNotes, saveConfiguration, configId,
        contactName, contactEmail, organizationName, countryRegion,
        desiredQuantity, desiredDeliveryDate, projectReference,
    } = useConfigurationStore();

    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [requestingSupplier, setRequestingSupplier] = useState(false);
    const [showSupplierModal, setShowSupplierModal] = useState(false);

    const canRequestSupplier = !!(window.astConfig && window.astConfig.canRequestSupplierQuotes);

    const handleSubmit = async () => {
        if (!contactEmail) {
            alert('Vul a.u.b. uw e-mailadres in bij Projectinformatie (stap 1).');
            return;
        }
        setSubmitting(true);
        const success = await saveConfiguration();
        setSubmitting(false);
        if (success) setSubmitted(true);
    };

    const handleRequestSupplier = async () => {
        if (!contactEmail) {
            alert('Vul a.u.b. uw e-mailadres in bij Projectinformatie (stap 1).');
            return;
        }
        setRequestingSupplier(true);
        let id = configId;
        if (!id) {
            const success = await saveConfiguration();
            setRequestingSupplier(false);
            if (!success) return;
            id = useConfigurationStore.getState().configId;
        } else {
            await saveConfiguration();
            setRequestingSupplier(false);
        }
        if (id) setShowSupplierModal(true);
    };

    if (submitted) {
        return (
            <div className="ast-summary">
                <div className="ast-summary__success">
                    <div className="ast-summary__success-icon">✓</div>
                    <h2>Bedankt voor uw aanvraag!</h2>
                    <p>Wij hebben uw configuratie ontvangen en nemen zo snel mogelijk contact met u op.</p>
                    {contactEmail && <p>Een bevestiging wordt verzonden naar <strong>{contactEmail}</strong>.</p>}
                </div>
            </div>
        );
    }

    return (
        <div className="ast-summary">
            <h2 className="ast-summary__title">Uw configuratie overzicht</h2>
            <p className="ast-summary__subtitle">Controleer uw selectie en vraag vrijblijvend een offerte aan.</p>

            {/* Project info */}
            {(organizationName || contactName || contactEmail) && (
                <div className="ast-summary__block">
                    <h3 className="ast-summary__block-title">Projectinformatie</h3>
                    <div className="ast-summary__info-grid">
                        {organizationName && <div><span className="ast-summary__label">Organisatie</span><span>{organizationName}</span></div>}
                        {contactName && <div><span className="ast-summary__label">Contactpersoon</span><span>{contactName}</span></div>}
                        {contactEmail && <div><span className="ast-summary__label">E-mail</span><span>{contactEmail}</span></div>}
                        {countryRegion && <div><span className="ast-summary__label">Land / Regio</span><span>{countryRegion}</span></div>}
                        {desiredQuantity > 0 && <div><span className="ast-summary__label">Aantal voertuigen</span><span>{desiredQuantity}</span></div>}
                        {desiredDeliveryDate && <div><span className="ast-summary__label">Gewenste leverdatum</span><span>{desiredDeliveryDate}</span></div>}
                        {projectReference && <div><span className="ast-summary__label">Projectreferentie</span><span>{projectReference}</span></div>}
                    </div>
                </div>
            )}

            {/* Selected options per category */}
            {categories.map((cat) => {
                const catOptions = (optionsByCategory[cat.id] || []).filter((opt) => selectedOptions[opt.id]);
                if (catOptions.length === 0) return null;

                return (
                    <div key={cat.id} className="ast-summary__block">
                        <h3 className="ast-summary__block-title">{cat.name}</h3>
                        <div className="ast-summary__lines">
                            {catOptions.map((opt) => {
                                const sel = selectedOptions[opt.id];
                                const line = priceBreakdown.lines?.find((l) => Number(l.option_id) === Number(opt.id));
                                return (
                                    <div key={opt.id} className="ast-summary__line">
                                        <div className="ast-summary__line-info">
                                            <span className="ast-summary__line-name">
                                                {opt.name}
                                                {sel?.quantity > 1 && <span className="ast-summary__qty"> × {sel.quantity}</span>}
                                            </span>
                                            {optionNotes[opt.id] && (
                                                <div className="ast-summary__line-note">
                                                    {renderNotes(opt, optionNotes[opt.id])}
                                                </div>
                                            )}
                                        </div>
                                        <span className="ast-summary__line-price">
                                            {line?.priceNone ? ''
                                                : line?.priceOnRequest ? <span className="ast-price-on-request">Op nacalculatie</span>
                                                : line?.priceIncluded ? <span className="ast-price-included">Inbegrepen</span>
                                                : formatPrice(line?.lineTotal || 0)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            {/* Totals */}
            <div className="ast-summary__totals">
                <div className="ast-summary__total-row">
                    <span>Subtotaal</span>
                    <span>{formatPrice(priceBreakdown.subtotal)}</span>
                </div>
                <div className="ast-summary__total-row ast-summary__total-row--muted">
                    <span>BTW ({priceBreakdown.vatPercentage}%)</span>
                    <span>{formatPrice(priceBreakdown.vatAmount)}</span>
                </div>
                <div className="ast-summary__total-row ast-summary__total-row--grand">
                    <span>Totaal</span>
                    <span>{formatPrice(priceBreakdown.total)}</span>
                </div>
            </div>

            {/* Notes */}
            <div className="ast-summary__block">
                <h3 className="ast-summary__block-title">Opmerkingen</h3>
                <textarea
                    value={configNotes}
                    onChange={(e) => setConfigNotes(e.target.value)}
                    className="ast-summary__textarea"
                    rows={3}
                    placeholder="Heeft u nog aanvullende wensen of opmerkingen?"
                />
            </div>

            {/* Submit */}
            <div className="ast-summary__submit">
                <button
                    className="ast-summary__submit-btn"
                    onClick={handleSubmit}
                    disabled={submitting || requestingSupplier}
                >
                    {submitting ? 'Aanvraag verzenden...' : 'Vrijblijvende offerte aanvragen'}
                </button>
                {canRequestSupplier && (
                    <button
                        className="ast-summary__supplier-btn"
                        onClick={handleRequestSupplier}
                        disabled={submitting || requestingSupplier}
                        style={{ marginTop: 12 }}
                    >
                        {requestingSupplier ? 'Bezig...' : 'Aanvraag versturen aan leverancier'}
                    </button>
                )}
                <p className="ast-summary__disclaimer">
                    Door op deze knop te klikken vraagt u vrijblijvend een offerte aan.
                    Wij nemen zo snel mogelijk contact met u op.
                </p>
            </div>

            {showSupplierModal && configId && (
                <RequestSupplierQuoteModal
                    configurationId={configId}
                    onClose={() => setShowSupplierModal(false)}
                />
            )}
        </div>
    );
}
